import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogIn, LogOut, User, Crown, History, ChevronDown, ShieldOff } from "lucide-react";
import { useAuth } from "@/lib/auth";
import NoAdsModal from "@/components/NoAdsModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact top-right sign-in button that flips to an avatar + menu when
 * the user is signed in. Shows a VIP crown for pioneer members and a
 * live "N of 20 pioneer slots left" nudge when signed out.
 */
export default function AuthPill({ accent = "#FF2E9A" }) {
  const { user, loading, signIn, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [vipRemaining, setVipRemaining] = useState(null);
  const [noAdsOpen, setNoAdsOpen] = useState(false);

  useEffect(() => {
    if (user) return;
    axios
      .get(`${API}/auth/vip-status`)
      .then((r) => setVipRemaining(r.data.vip_remaining))
      .catch(() => {});
  }, [user]);

  if (loading) return null;

  if (!user) {
    return (
      <div className="flex flex-col items-end gap-1 pointer-events-auto">
        <button
          type="button"
          onClick={() => signIn()}
          data-testid="signin-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs md:text-sm font-heading uppercase tracking-widest hover:bg-white/20 transition-colors"
        >
          <LogIn size={14} strokeWidth={3} />
          Sign In
        </button>
        {vipRemaining !== null && vipRemaining > 0 && (
          <div
            className="text-[9px] md:text-[10px] font-body uppercase tracking-widest px-2 py-0.5 rounded-full backdrop-blur-md"
            style={{ background: "rgba(0,0,0,0.4)", color: accent }}
            data-testid="vip-remaining-badge"
          >
            <Crown size={9} strokeWidth={3} className="inline mr-1" />
            {vipRemaining} founding-member badges left
          </div>
        )}
      </div>
    );
  }

  const initial = (user.name || user.email || "?").slice(0, 1).toUpperCase();
  return (
    <div className="relative pointer-events-auto">
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        data-testid="account-menu-btn"
        className="flex items-center gap-2 px-2 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-colors"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt="account"
            className="w-7 h-7 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center font-heading text-sm"
            style={{ background: accent, color: "#000" }}
          >
            {initial}
          </div>
        )}
        <span className="hidden md:inline font-body text-xs uppercase tracking-widest max-w-[140px] truncate">
          {user.name || user.email}
        </span>
        {user.is_vip && (
          <span
            className="hidden sm:flex items-center gap-1 text-[9px] font-heading uppercase tracking-widest px-1.5 py-0.5 rounded"
            style={{ background: "#A4E832", color: "#000" }}
            title={`Pioneer #${user.vip_rank ?? ""} — unlimited access`}
            data-testid="vip-badge"
          >
            <Crown size={10} strokeWidth={3} /> VIP
          </span>
        )}
        <ChevronDown size={14} strokeWidth={3} className="opacity-70" />
      </button>

      {menuOpen && (
        <div
          className="absolute right-0 mt-2 min-w-[220px] rounded-md border-2 bg-black/90 backdrop-blur-lg overflow-hidden"
          style={{ borderColor: accent }}
          data-testid="account-menu"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <div className="font-heading text-sm text-white truncate">
              {user.name}
            </div>
            <div className="font-body text-[10px] text-white/60 truncate uppercase tracking-widest">
              {user.email}
            </div>
            {user.is_vip && (
              <div
                className="mt-2 text-[10px] font-heading uppercase tracking-widest flex items-center gap-1"
                style={{ color: "#A4E832" }}
              >
                <Crown size={12} strokeWidth={3} />
                Founding member #{user.vip_rank}
              </div>
            )}
          </div>
          <Link
            to="/history"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-white font-body text-xs uppercase tracking-widest hover:bg-white/10"
            data-testid="menu-history-link"
          >
            <History size={14} strokeWidth={3} />
            My history
          </Link>
          {!user.ads_removed && (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setNoAdsOpen(true);
              }}
              data-testid="menu-remove-ads-btn"
              className="w-full flex items-center gap-2 px-4 py-2.5 font-body text-xs uppercase tracking-widest hover:bg-white/10"
              style={{ color: "#A4E832" }}
            >
              <ShieldOff size={14} strokeWidth={3} />
              Remove ads · $1.99/mo
            </button>
          )}
          {user.ads_removed && (
            <div
              className="w-full flex items-center gap-2 px-4 py-2.5 font-body text-xs uppercase tracking-widest opacity-70"
              data-testid="menu-adfree-status"
            >
              <ShieldOff size={14} strokeWidth={3} style={{ color: "#A4E832" }} />
              Ad-free active
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              signOut();
            }}
            data-testid="signout-btn"
            className="w-full flex items-center gap-2 px-4 py-2.5 text-white font-body text-xs uppercase tracking-widest hover:bg-white/10"
          >
            <LogOut size={14} strokeWidth={3} />
            Sign out
          </button>
        </div>
      )}
      <NoAdsModal open={noAdsOpen} onClose={() => setNoAdsOpen(false)} />
    </div>
  );
}
